<template>
  <div class="decision-container">
    <div v-if="canDelete" class="decision-container__delete-icon">
      <svg @click="deleteDecision" class="trash-icon">
        <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#trash" />
      </svg>
    </div>
    <div class="decision-container__drag-icon handle">
      <DragIcon color="#A9A9A9" class="icon-1-5x drag-icon" />
    </div>
    <div class="decision-container__clause">
      <TagBox value="if" />
      <div class="decision-container__condition">
        <ConditionGroupBuilder
          :value="providedDecision.conditions"
          :manager="decisionConditionsManager"
          :disabled="false"
          context="condition"
          @updated="conditionUpdated"
        ></ConditionGroupBuilder>
        <p v-if="isConditionsInvalid" class="help is-danger">Must not contain an invalid condition</p>
        <p v-else-if="isConditionsRequiredValidationInvalid" class="help is-danger">Must configure a condition</p>
      </div>
    </div>

    <div class="decision-container__transition">
      <TagBox value="then" />
      <div class="decision-container__transition-picker">
        <TransitionPicker
          :transition="providedDecision.transition"
          @transition-selected="transitionSelected"
        ></TransitionPicker>
        <p v-if="isTransitionPayloadValidationInvalid" class="help is-danger">Must not contain an invalid transition</p>
        <p v-else-if="isTransitionValidationInvalid" class="help is-danger">Must select a transition</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import ConditionGroupBuilder, { ConditionGroupUpdate } from '@/shared/components/conditions/ConditionGroupBuilder.vue';
import TagBox from '@/shared/components/TagBox.vue';
import TransitionPicker from '@/features/bots/components/designer/TransitionPicker.vue';
import DragIcon from '@/shared/components/DragIcon.vue';
import { TransitionViewModel } from '@/features/bots/models/designer/transitionViewModel';
import { DecisionViewModel } from '@/features/bots/models/designer/decisionViewModel';
import { required, minLength } from '@vuelidate/validators';
import { InputVariableViewModel } from '../../models/designer';
import { ConditionDataType, ConditionPickerOption } from '@/shared/components/conditions/models/conditions';
import { computed, nextTick, onMounted, PropType, ref, watch } from 'vue';
import { useStore } from 'vuex';
import useVuelidate from '@vuelidate/core';
import VuelidateUtil from '@shared/util/vuelidateUtil';
import {
  DecisionConditionsAttributeType,
  useDecisionConditions,
} from '@/features/bots/components/designer/Conditions/useDecisionConditions';

const props = defineProps({
  decision: {
    type: Object as PropType<DecisionViewModel>,
    required: true,
  },
  canDelete: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const flowVariables = computed<InputVariableViewModel[]>(() => store.getters.flowVariables);
const isTransitionPayloadInvalid = (payload: string) =>
  computed(() => store.getters.isTransitionPayloadInvalid(payload));

const decisionConditionsManager = useDecisionConditions();

const isInvalidCondition = ref(false);
const providedDecision = ref<DecisionViewModel>({
  conditions: {
    type: 'and',
    conditions: [],
  },
  transition: {
    payload: undefined,
    type: undefined,
  },
});

const validations = {
  providedDecision: {
    conditions: {
      conditions: {
        required,
        minLength: minLength(1),
        conditionValid() {
          return !isInvalidCondition.value;
        },
      },
    },
    transition: {
      required,
      type: {
        required,
      },
      payload: {
        payloadValid(payload: string) {
          return !payload || !isTransitionPayloadInvalid(payload).value;
        },
      },
    },
  },
};

const isConditionsRequiredValidationInvalid = computed(() =>
  VuelidateUtil.isRequiredInvalid($v, (v) => v.providedDecision.conditions.conditions),
);
const isConditionsInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.providedDecision.conditions.conditions, 'conditionValid'),
);
const $v = useVuelidate(validations, { providedDecision });
const isTransitionValidationInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.providedDecision.transition),
);
const isTransitionPayloadValidationInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.providedDecision.transition.payload, 'payloadValid'),
);

onMounted(() => {
  providedDecision.value = props.decision;
  checkValidTransition();
  setUpConditions();
});

watch(
  flowVariables,
  () => {
    setUpConditions();
    providedDecision.value = JSON.parse(JSON.stringify(providedDecision.value));
  },
  { immediate: true, deep: true },
);

const emit = defineEmits<{
  (e: 'updated', decision: DecisionViewModel, isInvalid: boolean): void;
  (e: 'deleted'): void;
}>();

function conditionUpdated(update: ConditionGroupUpdate) {
  isInvalidCondition.value = update.invalid;
  const clone = JSON.parse(JSON.stringify(providedDecision.value)); // to prevent updating the providedDecision which would kick off processing in child
  clone.conditions = update.conditionGroup;
  emit('updated', clone, $v.value.providedDecision.$invalid);
}

function deleteDecision() {
  emit('deleted');
}

function transitionSelected(transition: TransitionViewModel) {
  providedDecision.value.transition = transition;
  emit('updated', providedDecision.value, $v.value.providedDecision.$invalid);
}

function checkValidTransition() {
  if (!providedDecision.value.transition) {
    $v.value.providedDecision.transition!.$touch();
    nextTick(() => emit('updated', providedDecision.value, $v.value.providedDecision.$invalid));
  }
}

function setUpConditions() {
  decisionConditionsManager.setContextualConditions([
    ...flowVariables.value.map((flowVar) => {
      return {
        id: `flow.${flowVar.name}`,
        title: flowVar.name,
        type: ConditionDataType.Text,
        icon: 'FlowVariableIcon',
      } as ConditionPickerOption;
    }),
    {
      id: DecisionConditionsAttributeType.TimeOfDay,
      title: 'Time of day',
      type: ConditionDataType.DateTime,
      icon: 'DurationIcon',
    },
  ]);
}
</script>

<style scoped lang="scss">
.decision-container {
  position: relative;
  border: 1px rgb(226, 226, 226) solid;
  border-radius: 0.25rem;
  padding: 20px 40px 20px 20px;

  &__delete-icon {
    position: absolute;
    right: 10px;
    top: 10px;

    .trash-icon {
      width: 1.25em;
      height: 1.25em;
      fill: #c7cdd3;
      cursor: pointer;

      &:hover {
        fill: #a4a6a9;
      }
    }
  }

  &__clause {
    display: flex;
    gap: 30px;
    margin-bottom: 15px;
  }

  &__condition {
    width: 100%;
  }

  &__transition-picker {
    width: 100%;
  }

  &__transition {
    display: flex;
    gap: 30px;
  }

  &__drag-icon {
    cursor: move;
    visibility: hidden;
    display: flex;
    position: absolute;
    right: 8px;
    top: 0;
    bottom: 0;
  }

  &:hover {
    .drag-icon {
      visibility: visible;
    }
  }
}

:deep(.condition-button) {
  max-width: 400px;
  @media screen and (max-width: 1800px) {
    max-width: 300px;
  }
  @media screen and (max-width: 1600px) {
    max-width: 200px;
  }
}
</style>
